// Preview of a scheduled "Call performance" client report, from the frozen
// snapshot the backend returns (shape: Codex brief 2026-10-19 "scheduled
// report"). Compact on purpose: it sits inside the schedule dialog, the full
// layout is the emailed PDF and the Reporting → Calls tab.
import PropTypes from 'prop-types';
import { Alert, Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { formatSeconds } from '../callPerformance/callPerformanceModel';

const HEAD_SX = { fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', bgcolor: '#f8fafc' };
const CELL_SX = { fontSize: '13px' };
const n = (v) => (v == null ? 'Not available' : Number(v).toLocaleString('en-GB'));
const rate = (v) => (v == null ? 'Not available' : `${Number(v).toLocaleString('en-GB', { maximumFractionDigits: 1 })}%`);

export default function CallPerformancePreview({ snapshot }) {
  const group = snapshot.group || snapshot.sites?.[0] || null;
  const sites = snapshot.sites || [];
  const single = sites.length === 1 ? sites[0] : null;
  const metrics = group ? [
    ['Incoming calls', n(group.incoming?.total)],
    ['Answered', n(group.incoming?.answered)],
    ['Unanswered', group.incoming?.overflowed != null ? `${n(group.incoming?.unanswered)} (${n(group.incoming.overflowed)} overflowed)` : n(group.incoming?.unanswered)],
    ['Answer rate', rate(group.incoming?.answer_rate)],
    ['Outgoing calls', group.outgoing?.extensions ? `${n(group.outgoing?.total)} from ${group.outgoing.extensions} extensions` : n(group.outgoing?.total)],
    ['Average ring time', group.averages?.ring_seconds != null ? formatSeconds(group.averages.ring_seconds) : 'Not available'],
    ['Average incoming call', group.averages?.incoming_length_seconds != null ? formatSeconds(group.averages.incoming_length_seconds) : 'Not available'],
  ] : [];
  return (
    <Stack spacing={2} data-testid="call-performance-preview">
      <Typography variant="subtitle1">{snapshot.title} · {snapshot.period?.label}</Typography>
      <Typography variant="body2" color="text.secondary">
        {snapshot.source === 'webhook'
          ? 'Figures come from calls recorded live by the CRM; calls answered by the auto attendant and months before the phone system was connected are not included.'
          : `Figures come from the phone provider's call history. Timezone: ${snapshot.period?.timezone || 'Europe/London'}.`}
      </Typography>
      {!snapshot.deliverable && (
        <Alert severity="warning">Incomplete or unavailable data. Preview and downloads are available; email delivery is blocked.</Alert>
      )}
      {(snapshot.warnings || []).map((w) => <Alert severity="warning" key={typeof w === 'string' ? w : JSON.stringify(w)}>{typeof w === 'string' ? w : (w.message || w.code)}</Alert>)}
      {metrics.length > 0 && (
        <Table size="small" aria-label="Call performance summary">
          <TableBody>
            {metrics.map(([label, value]) => (
              <TableRow key={label}>
                <TableCell sx={{ ...CELL_SX, color: 'text.secondary', width: '55%' }}>{label}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: 700 }} align="right">{value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {sites.length > 1 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Incoming calls by site</Typography>
          <Table size="small" aria-label="Incoming calls by site">
            <TableHead><TableRow>{['Site', 'Incoming', 'Answered', 'Unanswered', 'Answer rate', 'Outgoing'].map((h, i) => <TableCell key={h} align={i ? 'right' : 'left'} sx={HEAD_SX}>{h}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {[...sites].sort((a, b) => (b.incoming?.answer_rate || 0) - (a.incoming?.answer_rate || 0)).map((s) => (
                <TableRow key={s.practice_id || s.name}>
                  <TableCell sx={CELL_SX}>{s.name}</TableCell>
                  <TableCell align="right" sx={CELL_SX}>{n(s.incoming?.total)}</TableCell>
                  <TableCell align="right" sx={CELL_SX}>{n(s.incoming?.answered)}</TableCell>
                  <TableCell align="right" sx={CELL_SX}>{n(s.incoming?.unanswered)}</TableCell>
                  <TableCell align="right" sx={{ ...CELL_SX, fontWeight: 700 }}>{rate(s.incoming?.answer_rate)}</TableCell>
                  <TableCell align="right" sx={CELL_SX}>{n(s.outgoing?.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {single && (single.incoming?.by_line || []).length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Incoming by patient line</Typography>
          <Table size="small" aria-label="Incoming by patient line">
            <TableHead><TableRow>{['Patient line', 'Calls', 'Answered', 'Missed', 'Rate'].map((h, i) => <TableCell key={h} align={i ? 'right' : 'left'} sx={HEAD_SX}>{h}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {single.incoming.by_line.map((l) => (
                <TableRow key={l.number || l.label}>
                  <TableCell sx={CELL_SX}>{l.label}{l.number && l.number !== l.label ? ` · ${l.number}` : ''}</TableCell>
                  <TableCell align="right" sx={CELL_SX}>{n(l.calls)}</TableCell>
                  <TableCell align="right" sx={CELL_SX}>{n(l.answered)}</TableCell>
                  <TableCell align="right" sx={CELL_SX}>{n(l.missed)}</TableCell>
                  <TableCell align="right" sx={{ ...CELL_SX, fontWeight: 700 }}>{rate(l.rate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {(group?.highlights || []).length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>What the data points to</Typography>
          {group.highlights.map((h) => <Typography key={h.title} variant="body2" sx={{ mb: 0.5 }}><strong>{h.title}</strong> — {h.body}</Typography>)}
        </Box>
      )}
      <Typography variant="caption" color="text.secondary">Unknown figures are shown as Not available, never as zero.</Typography>
    </Stack>
  );
}
CallPerformancePreview.propTypes = { snapshot: PropTypes.object.isRequired };
